import type { ZmagsEvent } from "./types";

/**
 * FSA Spender page `zmags` payloads (COM-250 AC3 / COM-658 AC3), as the legacy
 * page's Fastr script (`trackFilterEvent`) builds them. Pure: the component
 * spreads each result into `emitZmags(ZMAGS_EXP, ...event)`.
 */

/** Legacy price format: `$` + two decimals. */
export const formatZmagsPrice = (dollars: number): string =>
  `$${dollars.toFixed(2)}`;

/** Best Sellers is the only chip the legacy page ever sent an event for. */
const BEST_SELLERS = "best-sellers";

const bestSellerEvent = (on: boolean): ZmagsEvent => [
  "click_best_seller_filter",
  {
    scene: "Best Seller Filter",
    url: "Best Sellers",
    value: on ? "on" : "off",
  },
];

/**
 * A chip change from `prev` to `next`: the previous chip goes off, then the
 * new one on; the "All" chip (`all`) sends neither, and only Best Sellers sends.
 */
export const chipTransitionEvents = (
  prev: string,
  next: string,
  all: string,
): ZmagsEvent[] => {
  const events: ZmagsEvent[] = [];
  if (prev !== all && prev === BEST_SELLERS)
    events.push(bestSellerEvent(false));
  if (next !== all && next === BEST_SELLERS) events.push(bestSellerEvent(true));
  return events;
};

/** Grid scene, kept exactly as legacy sends it (trailing space included). */
const GRID_SCENE = "1 ";

/** A grid tile's PDP link click. */
export const gridLinkEvent = (href: string): ZmagsEvent => [
  "click_link",
  { scene: GRID_SCENE, url: href },
];

/** A grid tile's Quick View opened. */
export const gridQuickViewEvent = (productId: string): ZmagsEvent => [
  "click_quickview",
  { scene: GRID_SCENE, url: productId },
];

/**
 * An add to cart from a grid tile's Quick View. No scene, as legacy. A missing
 * or null price (CIO tiles can carry `null`, COM-343) sends an empty value.
 */
export const gridAddEvent = (
  productId: string,
  price: number | null | undefined,
): ZmagsEvent => [
  "click_add_to_cart",
  {
    url: productId,
    value: typeof price === "number" ? formatZmagsPrice(price) : undefined,
  },
];

/** The balance (max amount) submitted, in cents. */
export const priceFilterEvent = (cents: number): ZmagsEvent => [
  "change_price_filter",
  {
    scene: "Price Filter",
    url: "Max Amount",
    value: formatZmagsPrice(cents / 100),
  },
];
